package fr.frux.feature.treedetail.identity

import fr.frux.feature.treedetail.ImportedTreeSource
import fr.frux.shared.content.TreeCorrectionLimits
import fr.frux.shared.content.TreeRevisionVocabulary
import fr.frux.shared.domain.model.Certainty
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.TreeRevision
import fr.frux.shared.domain.model.TreeRevisionLog
import kotlin.math.roundToInt

enum class HistoryField { CREATION, SPECIES, POSITION, NOTE, OTHER }

enum class NoteMovement { ADDED, EDITED, CLEARED }

/** Ce qu'une ligne d'historique raconte. La note n'apparaît que par son mouvement, jamais par son texte. */
sealed interface HistoryChange {
    data class Created(
        val speciesName: String,
        val certainty: Certainty?,
    ) : HistoryChange

    data class SpeciesChanged(
        val from: String,
        val to: String,
    ) : HistoryChange

    data class CertaintyChanged(
        val from: Certainty?,
        val to: Certainty?,
    ) : HistoryChange

    data class Moved(
        val meters: Int,
    ) : HistoryChange

    data class NoteChanged(
        val movement: NoteMovement,
    ) : HistoryChange

    data object Redacted : HistoryChange

    /** Valeur inconnue de ce client (contrat ouvert) : ligne générique, jamais une erreur. */
    data class Unknown(
        val raw: String,
    ) : HistoryChange
}

sealed interface HistoryAuthor {
    data class Named(
        val name: String,
    ) : HistoryAuthor

    data object Moderation : HistoryAuthor

    data class Import(
        val source: ImportedTreeSource,
    ) : HistoryAuthor

    data object Unnamed : HistoryAuthor
}

data class HistoryRow(
    val field: HistoryField,
    val change: HistoryChange,
    val author: HistoryAuthor,
    val atMillis: Long,
    val reason: String?,
    val isCurrent: Boolean,
    val isRevert: Boolean,
    /** Nom scientifique de l'espèce en jeu (création, nouvelle espèce), `null` si inconnue du référentiel. */
    val scientificName: String?,
)

/**
 * Lignes d'historique dans l'ordre du serveur (la plus récente d'abord, elle seule est
 * [HistoryRow.isCurrent]). La ligne de création se reconnaît par son `kind`, jamais par la forme
 * de ses valeurs, et toute valeur inconnue donne une ligne générique.
 */
fun historyRows(
    log: TreeRevisionLog,
    speciesById: (Long) -> Species?,
    importSource: ImportedTreeSource?,
): List<HistoryRow> =
    log.revisions.mapIndexed { index, revision ->
        revision.toRow(index == 0, speciesById, importSource)
    }

private fun TreeRevision.toRow(
    isCurrent: Boolean,
    speciesById: (Long) -> Species?,
    importSource: ImportedTreeSource?,
): HistoryRow {
    val isRevert = kind == TreeRevisionVocabulary.KIND_REVERT
    val effective = if (isRevert) field else kind
    val historyField = historyFieldOf(effective)
    val decoded = if (redacted) null else decode(historyField, speciesById)
    return HistoryRow(
        field = historyField,
        change = if (redacted) HistoryChange.Redacted else decoded?.change ?: HistoryChange.Unknown(field ?: kind),
        author = authorOf(isRevert, importSource),
        atMillis = atMillis,
        reason = reason.takeUnless { redacted },
        isCurrent = isCurrent,
        isRevert = isRevert,
        scientificName = decoded?.scientificName,
    )
}

private fun historyFieldOf(name: String?): HistoryField =
    when (name) {
        TreeRevisionVocabulary.KIND_CREATION -> HistoryField.CREATION
        TreeRevisionVocabulary.KIND_SPECIES -> HistoryField.SPECIES
        TreeRevisionVocabulary.KIND_POSITION -> HistoryField.POSITION
        TreeRevisionVocabulary.KIND_NOTE -> HistoryField.NOTE
        else -> HistoryField.OTHER
    }

private fun TreeRevision.authorOf(
    isRevert: Boolean,
    importSource: ImportedTreeSource?,
): HistoryAuthor =
    when {
        isRevert || source == TreeRevisionVocabulary.SOURCE_ADMIN -> HistoryAuthor.Moderation
        source == TreeRevisionVocabulary.SOURCE_IMPORT && importSource != null -> HistoryAuthor.Import(importSource)
        authorName != null -> HistoryAuthor.Named(authorName!!)
        else -> HistoryAuthor.Unnamed
    }

private class Decoded(
    val change: HistoryChange,
    val scientificName: String? = null,
)

private fun TreeRevision.decode(
    historyField: HistoryField,
    speciesById: (Long) -> Species?,
): Decoded? =
    when (historyField) {
        HistoryField.CREATION -> decodeCreation(speciesById)
        HistoryField.SPECIES -> decodeSpecies(speciesById)
        HistoryField.POSITION -> decodePosition()
        HistoryField.NOTE -> decodeNote()
        HistoryField.OTHER -> null
    }

private fun speciesName(
    id: Long,
    speciesById: (Long) -> Species?,
): String = speciesById(id)?.commonNameFr ?: "Espèce n°$id"

private fun Map<String, String>.speciesId(): Long? = this[TreeRevisionVocabulary.KEY_SPECIES_ID]?.toLongOrNull()

/** `null` pour une certitude absente, [CertaintyRead.Invalid] pour une valeur que ce client ne connaît pas. */
private sealed interface CertaintyRead {
    data class Known(
        val value: Certainty?,
    ) : CertaintyRead

    data object Invalid : CertaintyRead
}

private fun Map<String, String>.certainty(): CertaintyRead {
    val raw = this[TreeRevisionVocabulary.KEY_CERTAINTY] ?: return CertaintyRead.Known(null)
    val parsed = Certainty.entries.firstOrNull { it.name == raw }
    return if (parsed == null) CertaintyRead.Invalid else CertaintyRead.Known(parsed)
}

private fun TreeRevision.decodeCreation(speciesById: (Long) -> Species?): Decoded? {
    val id = after.speciesId()
    val certainty = after.certainty() as? CertaintyRead.Known
    return if (id == null || certainty == null) {
        null
    } else {
        Decoded(
            change = HistoryChange.Created(speciesName(id, speciesById), certainty.value),
            scientificName = speciesById(id)?.scientificName,
        )
    }
}

private fun TreeRevision.decodeSpecies(speciesById: (Long) -> Species?): Decoded? {
    val from = before.speciesId()
    val to = after.speciesId()
    val certaintyFrom = (before.certainty() as? CertaintyRead.Known)?.value
    val certaintyTo = (after.certainty() as? CertaintyRead.Known)?.value
    val readable = before.certainty() is CertaintyRead.Known && after.certainty() is CertaintyRead.Known
    return when {
        from == null || to == null || !readable -> {
            null
        }

        from != to -> {
            Decoded(
                HistoryChange.SpeciesChanged(speciesName(from, speciesById), speciesName(to, speciesById)),
                speciesById(to)?.scientificName,
            )
        }

        certaintyFrom != certaintyTo -> {
            Decoded(HistoryChange.CertaintyChanged(certaintyFrom, certaintyTo))
        }

        else -> {
            null
        }
    }
}

private fun Map<String, String>.coordinates(): Pair<Double, Double>? {
    val latitude = this[TreeRevisionVocabulary.KEY_LATITUDE]?.toDoubleOrNull()?.takeIf { it.isFinite() }
    val longitude = this[TreeRevisionVocabulary.KEY_LONGITUDE]?.toDoubleOrNull()?.takeIf { it.isFinite() }
    return if (latitude != null && longitude != null) latitude to longitude else null
}

private fun TreeRevision.decodePosition(): Decoded? {
    val from = before.coordinates()
    val to = after.coordinates()
    return if (from == null || to == null) {
        null
    } else {
        val meters = TreeCorrectionLimits.distanceMeters(from.first, from.second, to.first, to.second)
        Decoded(HistoryChange.Moved(meters.roundToInt()))
    }
}

private fun TreeRevision.decodeNote(): Decoded? {
    val was = before[TreeRevisionVocabulary.KEY_NOTE_PRESENT]?.toBooleanStrictOrNull()
    val now = after[TreeRevisionVocabulary.KEY_NOTE_PRESENT]?.toBooleanStrictOrNull()
    val movement =
        when {
            was == null || now == null -> null
            !was && now -> NoteMovement.ADDED
            was && now -> NoteMovement.EDITED
            was -> NoteMovement.CLEARED
            else -> null
        }
    return movement?.let { Decoded(HistoryChange.NoteChanged(it)) }
}
